import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const { cartItems, clearCart, removeFromCart, updateQuantity } =
    useContext(CartContext);
  const [failedImages, setFailedImages] = useState([]);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      detailedItems.reduce(
        (total, item) =>
          total + Number(item.product.price) * Number(item.quantity),
        0
      ),
    [detailedItems]
  );

  const itemCount = detailedItems.reduce(
    (total, item) => total + Number(item.quantity),
    0
  );
  const shipping =
    subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standardRate;
  const total = subtotal + shipping;

  const markImageFailed = (source) => {
    setFailedImages((current) =>
      current.includes(source) ? current : [...current, source]
    );
  };

  if (detailedItems.length === 0) {
    return (
      <main className="cart-page" id="main-content">
        <header className="cart-page__header">
          <h1>Your Cart</h1>
          <span aria-label="0 items">0</span>
        </header>
        <section className="cart-page__empty">
          <p className="cart-page__empty-kicker">Your bag is currently empty.</p>
          <h2>Bring a thoughtful detail into your everyday.</h2>
          <p>
            Explore colourful objects for flowers, tables, desks and thoughtful
            gifts.
          </p>
          <Link className="cart-page__primary" to="/shop">
            Shop the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <header className="cart-page__header">
        <h1>Your Cart</h1>
        <span aria-label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}>
          {itemCount}
        </span>
      </header>

      <section className="cart-shipping" aria-label="Shipping rate">
        <div className="cart-shipping__copy">
          <p>
            Tracked delivery is {formatPrice(siteConfig.shipping.standardRate)}.
          </p>
          <span>Flat rate</span>
        </div>
        <div
          className="cart-shipping__track"
          aria-hidden="true"
        >
          <span style={{ width: "100%" }} />
        </div>
      </section>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-labelledby="cart-items-title">
          <div className="cart-page__items-head">
            <h2 id="cart-items-title">
              {itemCount} {itemCount === 1 ? "object" : "objects"}
            </h2>
            <button type="button" onClick={clearCart}>
              Clear cart
            </button>
          </div>

          <div className="cart-items-list">
            {detailedItems.map((item) => {
              const { product } = item;
              const image = product.images?.[0] || product.image;
              const stockLimit = Math.max(1, Number(product.stock) || 99);
              const lineTotal = Number(product.price) * item.quantity;
              return (
                <article
                  className="cart-line"
                  key={`${item.id}-${item.size}-${item.color}`}
                >
                  <Link
                    className="cart-line__image"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    {image && !failedImages.includes(image) ? (
                      <img
                        src={image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        onError={() => markImageFailed(image)}
                      />
                    ) : (
                      <span>Image unavailable</span>
                    )}
                  </Link>

                  <div className="cart-line__copy">
                    <Link to={`/shopDetails/${product.id}`}>
                      <h3>{product.name}</h3>
                    </Link>
                    <p className="cart-line__unit-price">
                      {formatPrice(product.price)} each
                    </p>
                    {(item.color || item.size) && (
                      <dl>
                        {item.color && (
                          <div>
                            <dt>Colour</dt>
                            <dd>{item.color}</dd>
                          </div>
                        )}
                        {item.size && (
                          <div>
                            <dt>Size</dt>
                            <dd>{item.size}</dd>
                          </div>
                        )}
                      </dl>
                    )}
                    <button
                      type="button"
                      className="cart-line__remove"
                      onClick={() =>
                        removeFromCart(item.id, item.size, item.color)
                      }
                    >
                      <FiTrash2 aria-hidden="true" /> Remove
                    </button>
                  </div>

                  <div className="cart-line__quantity">
                    <span className="cart-line__mobile-label">Quantity</span>
                    <div>
                      <button
                        type="button"
                        aria-label={`Decrease ${product.name} quantity`}
                        disabled={item.quantity <= 1}
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                      >
                        <FiMinus aria-hidden="true" />
                      </button>
                      <output
                        aria-live="polite"
                        aria-label={`${product.name} quantity ${item.quantity}`}
                      >
                        {item.quantity}
                      </output>
                      <button
                        type="button"
                        aria-label={`Increase ${product.name} quantity`}
                        disabled={item.quantity >= stockLimit}
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                      >
                        <FiPlus aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <p className="cart-line__total">
                    <span className="cart-line__mobile-label">Total</span>
                    {formatPrice(lineTotal)}
                  </p>
                </article>
              );
            })}
          </div>

          <Link className="cart-page__continue" to="/shop">
            <FiArrowLeft aria-hidden="true" /> Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="cart-summary-title">
          <h2 id="cart-summary-title">Order summary</h2>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatPrice(subtotal)}</dd>
            </div>
            <div>
              <dt>Tracked shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
            </div>
            <div className="cart-summary__total">
              <dt>Total</dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>
          <p className="cart-summary__note">
            Taxes are included. Duties and local taxes may apply outside the EU.
          </p>
          <Link className="cart-page__primary" to="/checkout">
            Checkout
          </Link>
          <p className="cart-summary__secure">Secure checkout · 30-day returns</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
